import { ReactNode, useState } from 'react';
import { faAngleDown, faLongArrowRight } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { cn } from '@v2/shared/lib/utils';
import Link from '@v2/shared/components/Link';
import Section from '@v2/shared/components/Section';
import { Category } from '@v2/shared/lib/types';
import CategoryItem from './CategoryItem';

const INITIALLY_VISIBLE = 12;

type Props = {
  title: string;
  categories: Category[];
  /** Passed to each item; see CategoryItem. */
  variant?: 'icon' | 'image';
  /**
   * Rendered under the grid, inside the same section.
   *
   * For the long tail a band does not have room to tile — Gifts & More puts its
   * non-featured categories here as text links. Inside the section rather than after
   * it, so they sit against the grid instead of a section's worth of margin away.
   */
  footer?: ReactNode;
  /**
   * A link at the right of the heading, drawn like a product band's "View all" — Gifts
   * & More's "All Gifts & More", to the listing behind every tile at once.
   */
  link?: { href: string; label: string };
};

/**
 * A grid of categories, truncated until asked for the rest.
 *
 * The twelve-item cut is headless's, and it is a display choice rather than a data one:
 * every category is already in the payload, so "Show All" reveals what is there instead
 * of fetching. That keeps the button instant and the markup complete for a crawler.
 */
export default function CategoryBand({ title, categories, variant = 'icon', footer, link }: Props) {
  const [showAll, setShowAll] = useState(false);
  const visible = showAll ? categories : categories.slice(0, INITIALLY_VISIBLE);

  if (categories.length === 0) {
    return null;
  }

  return (
    <Section>
      <Section.Header>
        <Section.Title>{title}</Section.Title>
        {link && (
          <Link href={link.href} className="text-xs font-semibold text-slate-600 hover:text-slate-900">
            {link.label} <FontAwesomeIcon icon={faLongArrowRight} />
          </Link>
        )}
      </Section.Header>
      <Section.Content>
        <ul
          className={cn(
            'grid gap-3 w-full auto-rows-fr',
            variant === 'image'
              ? 'grid-cols-2 md:grid-cols-3 lg:grid-cols-5'
              : 'grid-cols-2 md:grid-cols-4 lg:grid-cols-6'
          )}
        >
          {visible.map((category) => (
            // Keyed on id, not slug: `slug` is absent from the lean serializer group
            // these payloads use, so every key was undefined and React was matching
            // siblings by position.
            <CategoryItem key={category.id} category={category} variant={variant} />
          ))}
        </ul>
        {!showAll && categories.length > INITIALLY_VISIBLE && (
          <div className="flex items-center justify-center w-full my-4">
            <button
              type="button"
              onClick={() => setShowAll(true)}
              className="text-sm font-semibold text-slate-600 hover:text-slate-900 inline-flex items-center gap-2"
            >
              Show All
              <FontAwesomeIcon icon={faAngleDown} />
            </button>
          </div>
        )}
        {footer}
      </Section.Content>
    </Section>
  );
}
